import { getImageUrl } from "utils/helper";
import { PRODUCT } from "utils/constants";
import "../styles/thanksyouorderpopup.css";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from 'react';

// Dummy product data
const dummyProducts = [
  {
    productId: 1,
    product: {
      id: 1,
      name: "Wireless Bluetooth Headphones",
      imageUrl: "headphones.jpg",
      type: PRODUCT.TYPE.PHYSICAL,
      price: 2999
    },
    quantity: 2,
    sellingPriceTotal: 5998,
    sellingPricePerUnit: 2999
  },
  {
    productId: 2,
    product: {
      id: 2,
      name: "Smart Watch Pro",
      imageUrl: "smartwatch.jpg",
      type: PRODUCT.TYPE.PHYSICAL,
      price: 8999
    },
    quantity: 1,
    sellingPriceTotal: 8999,
    sellingPricePerUnit: 8999
  },
  {
    productId: 3,
    product: {
      id: 3,
      name: "Premium Home Cleaning Service",
      imageUrl: "cleaning.jpg",
      type: PRODUCT.TYPE.SERVICE,
      price: 2499
    },
    quantity: 1,
    sellingPriceTotal: 2499,
    sellingPricePerUnit: 2499
  }
];

// Dummy order details
const dummyOrderDetails = {
  orderId: "ORD123456",
  orderDate: new Date().toISOString(),
  orderItems: dummyProducts,
  totalAmount: dummyProducts.reduce((sum, item) => sum + item.sellingPriceTotal, 0),
  deliveryAddress: "123 Main Street, Bangalore, Karnataka 560001",
  paymentMethod: "Credit Card"
};

const ThankYouOrderPopup = ({
  createdOrderDetails = dummyOrderDetails, // Fallback to dummy data if not provided
  closeThankYouPopup
}) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [coordinates, setCoordinates] = useState({
    longitude: null,
    latitude: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [address, setAddress] = useState('');
  const [isLocationSaved, setIsLocationSaved] = useState(false);

   useEffect(() => {
    let mapInitialized = false;
    
    const loadMap = async (center) => {
      try {
        const { OlaMaps } = await import('olamaps-web-sdk');
        const olaMaps = new OlaMaps({
          apiKey: 'Oy5CCnfl6lPGJku8IyF0L5d4UQoLep8jblmHWFSa',
        });

        mapRef.current = olaMaps.init({
          style: "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
          container: mapContainer.current,
          center: center,
          zoom: 15,
        });

        mapRef.current.on('load', () => {
          const marker = olaMaps
            .addMarker({ 
              offset: [0, -15], 
              anchor: 'bottom', 
              draggable: true 
            })
            .setLngLat(center)
            .addTo(mapRef.current);
          
          markerRef.current = marker;

          const updateCoordinates = () => {
            const lngLat = marker.getLngLat();
            setCoordinates({
              longitude: parseFloat(lngLat.lng.toFixed(6)),
              latitude: parseFloat(lngLat.lat.toFixed(6))
            });
          };

          const popup = olaMaps
            .addPopup({ offset: [0, -15], anchor: 'bottom' })
            .setHTML('<div style="padding: 10px;"><h3 style="margin:0;">Drag Marker</h3><p>Move me to update coordinates</p></div>');
          
          marker.setPopup(popup);
          marker.on('drag', updateCoordinates);
          
          mapRef.current.on('click', (e) => {
            const { lng, lat } = e.lngLat;
            marker.setLngLat([lng, lat]);
            setCoordinates({
              longitude: parseFloat(lng.toFixed(6)),
              latitude: parseFloat(lat.toFixed(6))
            });
          });

          marker.getElement().addEventListener('click', (e) => {
            e.stopPropagation();
            marker.togglePopup();
          });
          
          setIsLoading(false);
        });
      } catch (error) {
        console.error('Error loading Ola Maps:', error);
        setError('Failed to load map');
        setIsLoading(false);
      }
    };

    const getLocationAndLoadMap = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              longitude: position.coords.longitude,
              latitude: position.coords.latitude
            };
            setCoordinates(userLocation);
            loadMap([userLocation.longitude, userLocation.latitude]);
          },
          (locationError) => {
            console.error('Error getting location:', locationError);
            setError('Could not get your location. Using default location.');
            // Fallback to default location (Bangalore)
            const defaultLocation = {
              longitude: 77.6165,
              latitude: 12.9314
            };
            setCoordinates(defaultLocation);
            loadMap([defaultLocation.longitude, defaultLocation.latitude]);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000, // 10 seconds
            maximumAge: 0 // Don't use cached position
          }
        );
      } else {
        setError('Geolocation is not supported by your browser. Using default location.');
        // Fallback to default location
        const defaultLocation = {
          longitude: 77.6165,
          latitude: 12.9314
        };
        setCoordinates(defaultLocation);
        loadMap([defaultLocation.longitude, defaultLocation.latitude]);
      }
    };

    getLocationAndLoadMap();

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (mapRef.current && markerRef.current && coordinates.latitude && coordinates.longitude) {
      mapRef.current.setCenter([coordinates.longitude, coordinates.latitude]);
      markerRef.current.setLngLat([coordinates.longitude, coordinates.latitude]);
    }
  }, [coordinates]);

  return (
    <motion.div
      className="thank-you-popup"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 50 }}
    >
      <div className="thank-you-content">
        <h2>Thank You for Your Order!</h2>
        <p>Your order #{createdOrderDetails.orderId} has been placed successfully on {new Date(createdOrderDetails.orderDate).toLocaleString()}.</p>
        
        <div className="order-details-container">
          <h3>Order Summary</h3>
          <ul className="order-details">
            {createdOrderDetails.orderItems.map((orderItem) => (
              <li key={orderItem.productId} className="order-item">
                <img 
                  src={getImageUrl(orderItem.product.imageUrl)} 
                  alt={orderItem.product.name} 
                  className="order-item-image" 
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = getImageUrl('default-product.jpg');
                  }}
                />
                <div className="order-item-info">
                  <span className="order-item-name">{orderItem.product.name}</span>
                  <span className="order-item-quantity">Qty: {orderItem.quantity}</span>
                  {orderItem.product.type === PRODUCT.TYPE.SERVICE ? (
                    <span className="order-item-type">Service</span>
                  ) : (
                    <span className="order-item-price">₹{orderItem.sellingPriceTotal.toLocaleString('en-IN')}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <div className="order-totals">
            <p className="total-amount">Total Amount: <span>₹{createdOrderDetails.totalAmount.toLocaleString('en-IN')}</span></p>
            <p className="payment-method">Payment Method: {createdOrderDetails.paymentMethod}</p>
            <p className="delivery-address">Delivery Address: {createdOrderDetails.deliveryAddress}</p>
          </div>
        </div>



 <div className="App">
      <h1>Ola Maps with Live Location</h1>
      
      {error ? (
        <div className="error-message">{error}</div>
      ) : isLoading ? (
        <h2>Getting your location...</h2>
      ) : (
        <h2>Longitude: {coordinates.longitude}, Latitude: {coordinates.latitude}</h2>
      )}
      
      <div 
        ref={mapContainer} 
        style={{ 
          width: '100%', 
          height: '500px',
          margin: '20px auto',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          display: isLoading ? 'none' : 'block'
        }} 
      />
      
      {isLoading && (
        <div className="loading-indicator">
          <p>Loading map and your location...</p>
          <div className="spinner"></div>
        </div>
      )}
    </div>

        {/* <div className="location-section">
          <h3>Please confirm your current location</h3>
          <p className="location-help-text">Drag the marker to your exact location to help our delivery partner</p>
          
          {error ? (
            <div className="error-message">{error}</div>
          ) : isLoading ? (
            <div className="loading-indicator">
              <p>Loading map and your location...</p>
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              <div className="coordinates-display">
                <span>Longitude: {coordinates.longitude}</span>
                <span>Latitude: {coordinates.latitude}</span>
              </div>
              <div className="address-display">
                <p>{address || 'Loading address...'}</p>
              </div>
            </>
          )}
          
          <div 
            ref={mapContainer} 
            className="map-container"
          />
          
          <button 
            className={`save-location-btn ${isLocationSaved ? 'saved' : ''}`}
            onClick={handleSaveLocation}
            disabled={!coordinates.longitude || !coordinates.latitude || isLocationSaved}
          >
            {isLocationSaved ? '✓ Location Saved!' : 'Confirm This Location'}
          </button>
        </div> */}
      </div>

      <button className="close-thank-you-popup-btn" onClick={closeThankYouPopup}>
        Close
      </button>
    </motion.div>
  );
};

export default ThankYouOrderPopup;